// TanStack hooks for the Releases UI. Centralising query keys + intervals
// here keeps the per-tab tables from each redeclaring identical queries
// (TanStack would dedupe by key anyway, but explicit hooks document intent
// and make refetch-interval changes a one-line fix).
//
// All hooks return the raw `useQuery` result so callers retain full access
// to `isPending`, `error`, `refetch`, etc.

import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { FLEET_STATUS_REFETCH_MS } from "@/lib/fleet-intervals";
import type {
	ChromiumBuildsResponse,
	FleetResponse,
	ManifestResponse,
	ProfileReleasesResponse,
	UpstreamResponse,
} from "./types.ts";

const MANIFEST_REFETCH_MS = 30_000;
const UPSTREAM_REFETCH_MS = 5 * 60_000;
const PROFILE_RELEASES_REFETCH_MS = 30_000;
const CHROMIUM_BUILDS_REFETCH_MS = 30_000;

export function useManifest() {
	return useQuery({
		queryKey: ["manifest"],
		queryFn: () => unwrap(api.api.manifest.get()) as Promise<ManifestResponse>,
		refetchInterval: MANIFEST_REFETCH_MS,
	});
}

export function useFleetStatus() {
	return useQuery({
		queryKey: ["workers"],
		queryFn: () => unwrap(api.api.workers.get()) as Promise<FleetResponse>,
		refetchInterval: FLEET_STATUS_REFETCH_MS,
	});
}

// Neutral chromium catalog from the chromium-catalog-poll mirror. The endpoint
// may not be shipped yet on older deployments — treat 404/501 as "empty
// catalog" so the UI can fall back to manual mode without a hard error.
export function useChromiumUpstream() {
	return useQuery({
		queryKey: ["chromium-upstream"],
		queryFn: async () => {
			const res = await fetch("/api/releases/chromium/upstream", {
				credentials: "same-origin",
			});
			if (!res.ok) {
				if (res.status === 404 || res.status === 501) {
					return {
						success: true,
						releases: [],
						syncedAt: null,
					} satisfies UpstreamResponse;
				}
				throw new Error(`upstream ${res.status}`);
			}
			return (await res.json()) as UpstreamResponse;
		},
		refetchInterval: UPSTREAM_REFETCH_MS,
		retry: false,
	});
}

export function useProfileReleases(browserMajor?: number) {
	return useQuery({
		queryKey: ["profile-releases", browserMajor ?? "all"],
		queryFn: async () => {
			const qs =
				browserMajor !== undefined
					? `?browserMajor=${encodeURIComponent(browserMajor)}`
					: "";
			const res = await fetch(`/api/releases/profiles${qs}`, {
				credentials: "same-origin",
			});
			if (!res.ok) throw new Error(`releases ${res.status}`);
			return (await res.json()) as ProfileReleasesResponse;
		},
		refetchInterval: PROFILE_RELEASES_REFETCH_MS,
	});
}

// ChromiumBuild rows for the operator surface. The endpoint sits inside
// the operator-role guard so viewer-role users get 401/403 — we surface
// that as an empty list rather than a hard error so the page still renders.
// The Activate column itself is gated on `canPublish` upstream so viewers
// never see the column the empty list would have populated anyway.
export function useChromiumBuilds(browserMajor?: number) {
	return useQuery({
		queryKey: ["chromium-builds", browserMajor ?? "all"],
		queryFn: async () => {
			const qs =
				browserMajor !== undefined
					? `?browserMajor=${encodeURIComponent(browserMajor)}`
					: "";
			const res = await fetch(`/api/releases/chromium/builds${qs}`, {
				credentials: "same-origin",
			});
			if (!res.ok) {
				if (res.status === 401 || res.status === 403) {
					return {
						success: true,
						builds: [],
					} satisfies ChromiumBuildsResponse;
				}
				throw new Error(`chromium-builds ${res.status}`);
			}
			return (await res.json()) as ChromiumBuildsResponse;
		},
		refetchInterval: CHROMIUM_BUILDS_REFETCH_MS,
		retry: false,
	});
}
